import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import logoImg from "../Images/logo40.jpg";
import groupImg from "../Images/164068220_331483578318504_917131076073981831_n.jpg";
import { supabase } from "../../lib/supabase";
import "./AboutUs.css";

const DEFAULT_GOALS = [
  { n: "01", title: "Promote & Develop",  body: "Promoting plastic art in Sudan, developing it and elevating the skills of artists." },
  { n: "02", title: "Cultural Impact",     body: "Highlighting the role of arts and their positive impact on Sudanese society and culture." },
  { n: "03", title: "Global Network",      body: "Expanding the network of communication between plastic artists in Sudan and the world." },
  { n: "04", title: "Showcase & Exchange", body: "Displaying the works of plastic artists, exchanging experiences and ideas in the field of art." },
  { n: "05", title: "Economic Support",    body: "Providing job opportunities and supporting plastic artists in Sudan." },
  { n: "06", title: "Artistic Awareness",  body: "Enhancing artistic awareness in Sudanese society and encouraging interest in arts." },
  { n: "07", title: "Heritage & Identity", body: "Expressing Sudanese culture and promoting it through plastic art." },
];

const SIZE_MAP = {
  full:      { width: '100%',  maxWidth: '100%' },
  large:     { width: '75%',   maxWidth: '75%'  },
  medium:    { width: '50%',   maxWidth: '50%'  },
  small:     { width: '250px', maxWidth: '250px' },
  thumbnail: { width: '120px', maxWidth: '120px' },
};

function SectionImage({ s, title, onClick }) {
  const size    = SIZE_MAP[s.image_size] || SIZE_MAP.full;
  const align   = s.image_align || 'center';
  const clickable = s.image_clickable;

  const marginStyle = align === 'left'   ? { marginRight: 'auto' }
                    : align === 'right'  ? { marginLeft:  'auto' }
                    : { margin: '0 auto' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems:
      align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
      marginBottom: 20 }}>
      <img
        src={s.image_url}
        alt={title || ''}
        onClick={clickable ? () => onClick(s.image_url) : undefined}
        style={{
          ...size,
          ...marginStyle,
          borderRadius: 10,
          objectFit: 'cover',
          display: 'block',
          cursor: clickable ? 'zoom-in' : 'default',
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={clickable ? (e) => {
          e.target.style.transform = 'scale(1.02)';
          e.target.style.boxShadow = '0 8px 32px rgba(0,0,0,0.4)';
        } : undefined}
        onMouseLeave={clickable ? (e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.boxShadow = 'none';
        } : undefined}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
      {s.image_caption && (
        <p style={{ fontSize: 12, color: '#777', marginTop: 6, textAlign: align === 'right' ? 'right' : align === 'left' ? 'left' : 'center' }}>
          {s.image_caption}
        </p>
      )}
    </div>
  );
}

function Lightbox({ src, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'zoom-out',
      }}
    >
      <img src={src} alt="" style={{ maxWidth: '92vw', maxHeight: '92vh', borderRadius: 8, objectFit: 'contain' }} />
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 20, right: 24,
          background: 'none', border: 'none', color: '#fff',
          fontSize: 28, cursor: 'pointer', lineHeight: 1,
        }}
      >✕</button>
    </div>
  );
}

export default function AboutUs() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar' || i18n.language?.startsWith('ar');
  const [cms,       setCms]       = useState({});
  const [lightbox,  setLightbox]  = useState(null);

  useEffect(() => {
    supabase
      .from("page_sections")
      .select("*")
      .eq("page", "about")
      .eq("is_visible", true)
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        if (data) {
          const map = {};
          data.forEach((s) => { map[s.section_key] = s; });
          setCms(map);
        }
      });
  }, []);

  const cBody = (key, defEn, defAr) => {
    const s = cms[key];
    if (!s) return isAr ? (defAr || defEn) : defEn;
    return isAr ? (s.body_ar || s.body_en || defAr || defEn) : (s.body_en || s.body_ar || defEn);
  };

  const cTitle = (key, defEn, defAr) => {
    const s = cms[key];
    if (!s) return isAr ? (defAr || defEn) : defEn;
    return isAr ? (s.title_ar || s.title_en || defAr || defEn) : (s.title_en || s.title_ar || defEn);
  };

  const cImg = (key, defSrc) => cms[key]?.image_url || defSrc;

  // Goals: from CMS body (one per line) or default array
  const goalItems = (() => {
    const s = cms["goals"];
    if (s) {
      const body = isAr ? (s.body_ar || s.body_en) : (s.body_en || s.body_ar);
      if (body) return body.split("\n").filter(Boolean).map((line, i) => ({
        n: String(i + 1).padStart(2, "0"),
        title: "",
        body: line.includes(":") ? line.split(":").slice(1).join(":").trim() : line,
        fullLine: line,
      }));
    }
    return DEFAULT_GOALS;
  })();

  // Extra CMS-only sections
  const HANDLED = new Set(["hero", "story", "quote", "goals", "save_sudan", "manifesto"]);
  const extraSections = Object.values(cms)
    .filter((s) => !HANDLED.has(s.section_key))
    .sort((a, b) => a.sort_order - b.sort_order);

  const paragraphs = (text) => text?.split("\n\n").filter(Boolean).map((p, i) => (
    <p key={i} className="about-body-text">{p}</p>
  ));

  return (
    <div className="about-page" dir={isAr ? "rtl" : "ltr"}>

      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="about-hero">
        <div className="about-hero-inner">
          <div className="about-hero-text">
            <p className="about-eyebrow">{isAr ? "عن المنظمة" : "About the Organisation"}</p>
            <h1 className="about-hero-title">
              <span className="about-hero-spar">SPAR</span>
              <br />
              {isAr ? "تجمع التشكيليين\nالسودانيين" : "Sudanese Plastic\nArtists Rally"}
            </h1>
            <p className="about-hero-subtitle">
              {cBody("hero",
                "Where visual artists in Sudan gather to showcase their works, exchange experiences, and resist through creativity — even in the darkest of times.",
                "حيث يجتمع الفنانون التشكيليون في السودان لعرض أعمالهم وتبادل الخبرات والمقاومة من خلال الإبداع."
              )}
            </p>
            <div className="about-hero-actions">
              <a href="/signup"      className="spar-btn spar-btn-primary">{isAr ? "انضم إلى تجمع" : "Join SPAR"}</a>
              <a href="/ArtistsList" className="spar-btn spar-btn-outline">{isAr ? "تعرف على الفنانين" : "Meet the Artists"}</a>
            </div>
          </div>
          <div className="about-hero-logo-col">
            <img
              src={cImg("hero", logoImg)}
              alt="SPAR Logo"
              className="about-hero-logo"
              onClick={cms.hero?.image_clickable ? () => setLightbox(cImg("hero", logoImg)) : undefined}
              style={{ cursor: cms.hero?.image_clickable ? "zoom-in" : "default" }}
            />
            <div className="about-ar-name">تجمع التشكيليين السودانيين</div>
          </div>
        </div>
      </section>

      {/* ── Story / What is SPAR ─────────────────────────────────────────── */}
      <section className="about-section about-mission-section">
        <div className="about-mission-grid">
          <div>
            <p className="about-eyebrow">{isAr ? "قصتنا" : "Our Story"}</p>
            <h2 className="about-section-title">{cTitle("story", "What is SPAR?", "ما هو تجمع التشكيليين السودانيين؟")}</h2>
            {cms.story?.image_url && (cms.story.image_align === 'top' || !cms.story.image_align) && (
              <SectionImage s={cms.story} title={cTitle("story", "SPAR", "")} onClick={setLightbox} />
            )}
            {paragraphs(cBody("story",
              "The Sudanese Plastic Artists Rally is where visual artists in Sudan gather to showcase their works, exchange experiences and ideas in the artistic field. This association aims to enhance visual art in Sudan, raise awareness of the importance of arts and their impact on society.\n\nHowever, participants in the Sudanese Plastic Artists Rally may face political costs, as art can be a driving force for change in society.\n\n\"Artists in Sudan have been oppressed for so long,\" and they were specifically targeted by the government during the protests. But the revolution brought artists together for the first time, and their art kept them united.",
              "تجمع التشكيليين السودانيين هو حيث يجتمع الفنانون التشكيليون في السودان لعرض أعمالهم وتبادل الخبرات والأفكار في المجال الفني.\n\n\"لقد قُمع الفنانون في السودان لفترة طويلة\"، لكن الثورة جمعتهم لأول مرة وظل فنهم يوحدهم."
            ))}
            {cms.story?.image_url && cms.story.image_align === 'bottom' && (
              <SectionImage s={cms.story} title={cTitle("story", "SPAR", "")} onClick={setLightbox} />
            )}
          </div>
          <div className="about-mission-aside">
            <div className="about-quote-card">
              <div className="about-quote-bar" />
              <p className="about-quote-text">
                {cBody("quote",
                  '"The field of Visual Arts in Sudan is lagging behind compared to the rest of the world mainly because we have no spaces to show our works."',
                  '"مجال الفنون البصرية في السودان متأخر مقارنة ببقية العالم، أساساً لأنه لا توجد مساحات لعرض أعمالنا."'
                )}
              </p>
              <span className="about-quote-attr">
                {cTitle("quote", "— Sudanese Artist, 2019", "— فنان سوداني، 2019")}
              </span>
            </div>
            <div className="about-download-block">
              <p className="about-download-title">{isAr ? "الوثائق الرسمية" : "Official Documents"}</p>
              <a href="/public/Images/DOC-20221014-WA0038.pdf" download className="about-download-link">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {isAr ? "تحميل النظام الأساسي" : "Download Bylaws"}
              </a>
              <a href="/public/Images/DOC-20221014-WA0039.pdf" download className="about-download-link">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {isAr ? "تحميل عقد التأسيس" : "Download Articles of Association"}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Goals ────────────────────────────────────────────────────────── */}
      <section className="about-goals-section">
        <div className="about-goals-inner">
          <div className="about-goals-header">
            <p className="about-eyebrow">{isAr ? "هدفنا" : "Our Purpose"}</p>
            <h2 className="about-section-title">{cTitle("goals", "What SPAR Aims to Achieve", "ما يسعى التجمع إلى تحقيقه")}</h2>
          </div>
          <div className="about-goals-grid">
            {goalItems.map((g) => (
              <div key={g.n} className="about-goal-card">
                <span className="about-goal-num">{g.n}</span>
                {g.title && <h3 className="about-goal-title">{g.title}</h3>}
                <p className="about-goal-body">{g.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Save Sudan ───────────────────────────────────────────────────── */}
      <section className="about-save-section">
        <div className="about-save-inner">
          <div className="about-save-img-col">
            <img
              src={cImg("save_sudan", groupImg)}
              alt="Sudanese Artists — Save Sudan"
              className="about-save-img"
              onClick={cms.save_sudan?.image_clickable ? () => setLightbox(cImg("save_sudan", groupImg)) : undefined}
              style={{ cursor: cms.save_sudan?.image_clickable ? "zoom-in" : "default" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
          <div className="about-save-text-col">
            <div className="about-save-badge">{isAr ? "حملة" : "CAMPAIGN"}</div>
            <h2 className="about-section-title">{cTitle("save_sudan", "Save Sudan", "أنقذوا السودان")}</h2>
            <h3 className="about-save-sub">{isAr ? "نداء الفنان" : "The Call of the Artist"}</h3>
            {paragraphs(cBody("save_sudan",
              "«Murals: Keeping Eyes on the Revolution»\nDuring Sudan's 2019 revolution — as people mobilized across the country with sit-ins, marches, boycotts, and strikes — artists helped capture the country's discontent and solidify protesters' resolve.\n\nStop this bloody War. #No_to_War #Save_Sudan",
              "الجداريات: إبقاء الأعين على الثورة\nخلال ثورة السودان عام 2019، ساعد الفنانون في التعبير عن سخط الشعب وتعزيز عزيمة المحتجين.\n\nأوقفوا هذه الحرب الدموية. #لا_للحرب #أنقذوا_السودان"
            ))}
            <a
              href={cms.save_sudan?.link_url || "https://sponsorasudaneseartist.wordpress.com/meet-the-artists/"}
              target="_blank" rel="noopener noreferrer"
              className="spar-btn spar-btn-green"
            >
              {isAr
                ? (cms.save_sudan?.link_label_ar || cms.save_sudan?.link_label_en || "رعاية فنان سوداني")
                : (cms.save_sudan?.link_label_en || "Sponsor a Sudanese Artist")}
            </a>
          </div>
        </div>
      </section>

      {/* ── Manifesto ────────────────────────────────────────────────────── */}
      <section className="about-manifesto-section">
        <div className="about-manifesto-inner">
          <div className="about-manifesto-flag"><span /><span /><span /></div>
          <h2 className="about-manifesto-title">{cTitle("manifesto", "We Are the Strength ✊", "نحن القوة ✊")}</h2>
          {paragraphs(cBody("manifesto",
            "Collectively or individually, we must all engage in civil efforts to halt the war and hold accountable those who have planned, collaborated, participated in, or supported it in any way.\n\nWe are the people, and we are the strength. We are the #People_Power and the holders of the will. #No_to_War #Save_Sudan",
            "جماعياً أو فردياً، يجب علينا المشاركة في الجهود المدنية لوقف الحرب ومحاسبة المسؤولين.\n\nنحن الشعب ونحن القوة. #لا_للحرب #أنقذوا_السودان"
          ))}
          <a
            href={cms.manifesto?.link_url || "https://www.swp-berlin.org/publikation/stopping-the-war-in-sudan"}
            target="_blank" rel="noopener noreferrer"
            className="spar-btn spar-btn-glass"
          >
            {isAr
              ? (cms.manifesto?.link_label_ar || cms.manifesto?.link_label_en || "اقرأ المزيد ←")
              : (cms.manifesto?.link_label_en || "Learn More →")}
          </a>
        </div>
      </section>

      {/* ── Extra CMS sections ───────────────────────────────────────────── */}
      {extraSections.map((s) => {
        const title    = isAr ? (s.title_ar || s.title_en) : (s.title_en || s.title_ar);
        const body     = isAr ? (s.body_ar  || s.body_en)  : (s.body_en  || s.body_ar);
        const btnLabel = isAr ? (s.link_label_ar || s.link_label_en) : (s.link_label_en || s.link_label_ar);
        const showImgTop = s.image_url && (s.image_align === 'top' || s.image_align === 'left' || !s.image_align);
        const showImgBot = s.image_url && (s.image_align === 'bottom' || s.image_align === 'right');

        return (
          <section key={s.id} className="about-section">
            <div className="about-goals-inner">
              {title && <h2 className="about-section-title">{title}</h2>}
              {showImgTop && <SectionImage s={s} title={title} onClick={setLightbox} />}
              {body && paragraphs(body)}
              {showImgBot && <SectionImage s={s} title={title} onClick={setLightbox} />}
              {s.link_url && (
                <a href={s.link_url} target={s.link_target || "_self"}
                   rel={s.link_target === "_blank" ? "noopener noreferrer" : undefined}
                   className="spar-btn spar-btn-primary" style={{ marginTop: 12 }}>
                  {btnLabel || (isAr ? "اقرأ المزيد" : "Learn More")}
                </a>
              )}
            </div>
          </section>
        );
      })}

    </div>
  );
}
